import {capitalize, compact, flatMap, uniq} from 'es-toolkit'
import invariant from 'invariant'
import {useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../hooks/redux'
import {fatalError} from '../redux/features/core'
import {
  openConfirmDeleteModal,
  openCreateProductionTimeModal,
  openEditProductionTimesModal,
} from '../redux/features/modal'

import useAsync from '../../../commons/src/hooks/use-async'
import useSelectedIds from '../hooks/use-selected-ids'
import useSortAttributes, {SortDefinition} from '../hooks/use-sort-attributes'
import {formatFactorToPercentage, formatPrice, formatTimeRange} from '../libs/formatter'
import * as optionApi from '../services/api/option'
import * as providerInfoApi from '../services/api/provider-info'
import {goToProviderInfos} from '../services/navigation'

import {
  DefaultProductionTimes,
  OptionId,
  OptionWithLinkedProviderInfos,
  ProviderInfo,
} from '../types'

import deleteIcon from '../../../commons/src/assets/icons/delete.svg'
import editIcon from '../../../commons/src/assets/icons/edit.svg'
import tableSearchIcon from '../../../commons/src/assets/icons/table-search.svg'

import ActionBar from '../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../commons/src/components/button'
import CheckboxField from '../../../commons/src/components/checkbox-field'
import Headline from '../../../commons/src/components/headline'
import Icon from '../../../commons/src/components/icon'
import LinkButton from '../../../commons/src/components/link-button'
import ActionHeaderLayout from '../components/action-header-layout'
import ActionHeader from '../components/action-header-layout/action-header'
import Container from '../components/container'
import PageContentLayout from '../components/page-content-layout'
import PageHeader from '../components/page-content-layout/PageHeader'
import Table from '../components/table'
import TableCell from '../components/table/TableCell'
import TableHeaderCell from '../components/table/TableHeaderCell'
import TableRow from '../components/table/TableRow'

import PagePartial from './page-partial'

type TableRowContent = OptionWithLinkedProviderInfos & {
  name: string
  basePrice: number
  factor: number
  productionTimeFastTotal: number
  productionTimeSlowTotal: number
}

const SORT_ATTRIBUTES: SortDefinition<TableRowContent> = {
  name: {
    asc: ({name: a}, {name: b}) => a.localeCompare(b),
    desc: ({name: a}, {name: b}) => b.localeCompare(a),
  },
  type: {
    asc: ({type: a}, {type: b}) => a.localeCompare(b),
    desc: ({type: a}, {type: b}) => b.localeCompare(a),
  },
  basePrice: {
    asc: ({basePrice: a}, {basePrice: b}) => a - b,
    desc: ({basePrice: a}, {basePrice: b}) => b - a,
  },
  productionTime: {
    asc: ({productionTimeSlowTotal: a}, {productionTimeFastTotal: b}) => a - b,
    desc: ({productionTimeFastTotal: a}, {productionTimeSlowTotal: b}) => b - a,
  },
  factor: {
    asc: ({factor: a}, {factor: b}) => a - b,
    desc: ({factor: a}, {factor: b}) => b - a,
  },
  linkedConfigs: {
    asc: ({providerInfos: a}, {providerInfos: b}) => a.length - b.length,
    desc: ({providerInfos: a}, {providerInfos: b}) => b.length - a.length,
  },
}

const getLinkedConfigIds = (
  productionTimeOptions: OptionWithLinkedProviderInfos[],
  selectedIds: OptionId[]
) => {
  const selectedProductionTimeOptions = productionTimeOptions.filter(({id}) =>
    selectedIds.includes(id)
  )
  return uniq(
    flatMap(selectedProductionTimeOptions, option =>
      option.providerInfos.map(providerInfo => providerInfo.id)
    )
  )
}

const getDefaultProductionTimes = (
  productionTimeOptions: OptionWithLinkedProviderInfos[],
  selectedIds: OptionId[],
  providerInfos: ProviderInfo[]
): DefaultProductionTimes | undefined => {
  const linkedConfigIds = getLinkedConfigIds(productionTimeOptions, selectedIds)

  // If there are no linked config ids, there are no default production times
  if (linkedConfigIds.length === 0) {
    return undefined
  }

  const linkedProviderInfos = providerInfos.filter(({id}) => linkedConfigIds.includes(id))

  return {
    productionTimeFast: Math.max(
      ...compact(linkedProviderInfos.map(providerInfo => providerInfo.productionTimeFast))
    ),
    productionTimeSlow: Math.max(
      ...compact(linkedProviderInfos.map(providerInfo => providerInfo.productionTimeSlow))
    ),
  }
}

const ProductionTimesPage = () => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()

  const {
    isLoading,
    data: productionTimeOptions = [],
    refresh,
  } = useAsync(
    () => optionApi.getProductionTimeOptionsWithLinkedProviderInfosByProvider(provider.id),
    [],
    {
      onError: error => dispatch(fatalError({error})),
      defaultLoading: true,
    }
  )

  const {data: providerInfos = []} = useAsync(
    () => providerInfoApi.getByProvider(provider.id),
    [],
    {
      onError: error => dispatch(fatalError({error})),
    }
  )

  const {toggleId, toggleAll, removeIds, isSelected, isAnySelected, selectedIds} =
    useSelectedIds<OptionId>(() => productionTimeOptions.map(option => option.id))

  const refreshAndDeselect = () => {
    refresh()
    removeIds(selectedIds)
  }

  const tableRows: TableRowContent[] = productionTimeOptions.map(option => ({
    ...option,
    name: option.name || '-',
    basePrice: option.basePrice || 0,
    factor: option.factor || 0,
    productionTimeFastTotal: option.productionTimeFastTotal || 0,
    productionTimeSlowTotal: option.productionTimeSlowTotal || 0,
  }))

  const {
    sortedItems: sortedTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(tableRows, SORT_ATTRIBUTES, 'name')

  const renderRow = (tableRow: TableRowContent) => (
    <TableRow key={tableRow.id}>
      <TableCell>
        <ActionBar>
          <CheckboxField checked={isSelected(tableRow.id)} onChange={() => toggleId(tableRow.id)} />
        </ActionBar>
      </TableCell>
      <TableCell strong>{tableRow.name}</TableCell>
      <TableCell>{capitalize(tableRow.type)}</TableCell>
      <TableCell>{tableRow.factor ? formatFactorToPercentage(tableRow.factor) : '-'}</TableCell>
      <TableCell>
        {tableRow.basePrice === 0 ? '-' : formatPrice(tableRow.basePrice, provider.currency)}
      </TableCell>
      <TableCell>
        {formatTimeRange(tableRow.productionTimeFastTotal, tableRow.productionTimeSlowTotal)}
      </TableCell>
      <TableCell strong error={tableRow.providerInfos.length === 0}>
        {tableRow.providerInfos.length > 0 ? (
          <LinkButton
            icon={<Icon source={tableSearchIcon} />}
            iconAlign="right"
            onClick={() =>
              goToProviderInfos(navigate, {
                initialFilters: {
                  productionTime: [tableRow.id],
                },
              })
            }
          >
            <strong>{tableRow.providerInfos.length}</strong>
          </LinkButton>
        ) : (
          <strong>{tableRow.providerInfos.length}</strong>
        )}
      </TableCell>
    </TableRow>
  )

  const linkedConfigsCount = getLinkedConfigIds(productionTimeOptions, selectedIds).length

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={<Headline size="l" label="Production time options" />}
            actions={
              <Button
                onClick={() =>
                  dispatch(
                    openCreateProductionTimeModal({
                      linkedConfigsCount: 0,
                      defaultProductionTimes: undefined,
                      onDone: refreshAndDeselect,
                    })
                  )
                }
              >
                Create new option
              </Button>
            }
          />
        }
      >
        <Container>
          <ActionHeaderLayout
            header={
              <ActionHeader
                left={
                  <LinkButton onClick={() => toggleAll()}>
                    {isAnySelected() ? 'Deselect all' : 'Select all'}
                  </LinkButton>
                }
                right={
                  <>
                    <LinkButton
                      size="200"
                      icon={<Icon source={editIcon} title="Edit" />}
                      disabled={selectedIds.length === 0}
                      onClick={() =>
                        dispatch(
                          openEditProductionTimesModal({
                            productionTimeOptions: productionTimeOptions.filter(({id}) =>
                              selectedIds.includes(id)
                            ),
                            linkedConfigsCount,
                            defaultProductionTimes: getDefaultProductionTimes(
                              productionTimeOptions,
                              selectedIds,
                              providerInfos
                            ),
                            onDone: refreshAndDeselect,
                          })
                        )
                      }
                    />
                    <LinkButton
                      size="200"
                      icon={<Icon source={deleteIcon} title="Delete" />}
                      disabled={selectedIds.length === 0}
                      onClick={() => {
                        dispatch(
                          openConfirmDeleteModal({
                            itemsToDeleteCount: selectedIds.length,
                            onConfirm: () => {
                              optionApi.removeMultiple(selectedIds).catch(fatalError).then(refresh)
                            },
                            linkedConfigsCount,
                          })
                        )
                      }}
                    />
                  </>
                }
              />
            }
          >
            <Table
              isLoading={isLoading}
              empty="No options have been added yet."
              header={
                <TableRow>
                  <TableHeaderCell width="28px" />
                  <TableHeaderCell
                    width={2}
                    sort={sortAttribute === 'name' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('name')}
                  >
                    Name
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    sort={sortAttribute === 'type' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('type')}
                  >
                    Type
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    align="right"
                    sort={sortAttribute === 'factor' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('factor')}
                  >
                    Additional production cost
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={1}
                    align="right"
                    sort={sortAttribute === 'basePrice' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('basePrice')}
                  >
                    Base price
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={2}
                    align="right"
                    sort={sortAttribute === 'productionTime' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('productionTime')}
                  >
                    Total production time
                  </TableHeaderCell>
                  <TableHeaderCell
                    width={4}
                    align="right"
                    sort={sortAttribute === 'linkedConfigs' && sortOrder ? sortOrder : undefined}
                    onClick={() => toggleSortBy('linkedConfigs')}
                  >
                    Linked materials
                  </TableHeaderCell>
                </TableRow>
              }
            >
              {sortedTableRows.map(renderRow)}
            </Table>
          </ActionHeaderLayout>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default ProductionTimesPage
